import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { POLICIES, POLICY_ORDER, POLICIES_APPROVED } from "../data/policies";
import { AnalyticsOptOut } from "../components/AnalyticsOptOut";

// Renders policy text; {{D:...}} = owner decision (highlighted until approved), {{L:...}} = attorney-review note (draft only),
// {{CONTROL:analytics-opt-out}} = the live analytics opt-out control.
const TOKEN = /\{\{(D|L|CONTROL):(.*?)\}\}/g;

export const renderPolicyText = (text) => {
  const parts = [];
  let last = 0;
  for (const m of text.matchAll(TOKEN)) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[1] === "D") {
      parts.push(<mark key={m.index} className="bg-yellow-300/20 text-yellow-200 px-1 rounded-sm" data-testid="policy-decision">[DECISION: {m[2]}]</mark>);
    } else if (m[1] === "CONTROL" && m[2] === "analytics-opt-out") {
      parts.push(<AnalyticsOptOut key={m.index} />);
    } else if (m[1] === "L" && !POLICIES_APPROVED) {
      parts.push(<em key={m.index} className="block mt-2 text-xs text-sky-300/80" data-testid="policy-legal-flag">Counsel review: {m[2]}.</em>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

const PolicyPage = () => {
  const { slug } = useParams();
  const policy = POLICIES[slug];
  if (!policy) return <Navigate to="/404" replace />;
  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-28 pb-24">
      <Helmet><title>{`${policy.title} · Highwall Lift`}</title>{!POLICIES_APPROVED && <meta name="robots" content="noindex,nofollow" />}</Helmet>
      <div className="max-w-3xl mx-auto px-6 sm:px-8">
        <p className="font-mono text-signal-red uppercase tracking-[0.2em] text-xs font-bold mb-3">Highwall Lift</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight uppercase text-white mb-3" data-testid="policy-title">{policy.title}</h1>
        <p className="text-gray-400 mb-8">{policy.summary}</p>
        {!POLICIES_APPROVED && (
          <div className="border border-yellow-300/40 bg-yellow-300/10 p-4 mb-8 text-sm text-yellow-100" data-testid="policy-draft-banner">
            <span className="font-mono text-xs uppercase tracking-[0.2em] font-bold">Draft — pending approval.</span> Highlighted items are business decisions Highwall Lift must confirm before publication. Until then, contact <a href="mailto:sales@highwalllift.com" className="underline">sales@highwalllift.com</a> with questions.
          </div>
        )}
        <nav className="flex flex-wrap gap-x-5 gap-y-2 mb-10 font-mono text-xs uppercase tracking-widest" data-testid="policy-nav">
          {POLICY_ORDER.map((s) => (
            <Link key={s} to={`/policies/${s}`} className={s === slug ? "text-signal-red" : "text-gray-500 hover:text-white"} data-testid={`policy-nav-${s}`}>{POLICIES[s].title}</Link>
          ))}
        </nav>
        <article className="space-y-8" data-testid="policy-body">
          {policy.sections.map((sec) => (
            <section key={sec.h}>
              <h2 className="font-heading text-base md:text-lg font-bold uppercase tracking-wide text-white mb-3">{sec.h}</h2>
              {sec.p.map((para, i) => para.includes("{{CONTROL:")
                ? <div key={i} className="text-gray-300 text-sm sm:text-base leading-relaxed mb-3">{renderPolicyText(para)}</div>
                : <p key={i} className="text-gray-300 text-sm sm:text-base leading-relaxed mb-3">{renderPolicyText(para)}</p>)}
            </section>
          ))}
        </article>
        <Link to="/" className="inline-block mt-12 text-gray-400 hover:text-white text-sm uppercase tracking-wide">Back to Home</Link>
      </div>
    </div>
  );
};

export default PolicyPage;
